/**
 * useLenis.js — Aviatricks Aero Labs
 * Smooth scroll on desktop AND mobile.
 * Uses lenis v1.3+
 *
 * Install: npm install lenis
 * Remove:  npm uninstall @studio-freight/lenis
 */

import Lenis from "lenis";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import gsap from "gsap";

let lenisInstance = null;
let tickerFn = null;

export function initLenis() {
  // FIX (scroll restoration): Tell the browser not to auto-restore scroll
  // position on refresh/navigation — we handle it ourselves in ScrollToTop.
  // Must be set before Lenis initialises so it can't race with the browser.
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }

  const isMobile = window.innerWidth <= 768;

  lenisInstance = new Lenis({
    duration: isMobile ? 0.8 : 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
    wheelMultiplier: 1.0,
    touchMultiplier: isMobile ? 2 : 1.5,
    // FIX (mobile scroll lock): REMOVED touchAction: "none".
    // Lenis was stamping `touch-action: none` on document.documentElement,
    // which told the browser to hand ALL touch gestures to JS and never
    // scroll natively. On mobile this meant GSAP's pin spacer was
    // unreachable — users couldn't scroll past the AboutHero section at all.
    // Without this option, Lenis defaults to "none" internally but handles
    // the gestures correctly through its own RAF loop without locking the
    // page when a GSAP pin spacer is present.
    prevent: (node) => {
      // Keep Lenis off elements that manage their own scroll (e.g. modals).
      return node.classList && node.classList.contains("no-smooth");
    },
  });

  // Sync with GSAP ScrollTrigger
  lenisInstance.on("scroll", ScrollTrigger.update);

  tickerFn = (time) => {
    if (lenisInstance) lenisInstance.raf(time * 1000);
  };

  gsap.ticker.add(tickerFn);
  gsap.ticker.lagSmoothing(0);

  // Ensure we start at top on page load/refresh
  setTimeout(() => {
    if (lenisInstance) {
      lenisInstance.scrollTo(0, { immediate: true, force: true });
    }
    window.scrollTo(0, 0);
  }, 50);

  return lenisInstance;
}

export function getLenis() {
  return lenisInstance;
}

export function destroyLenis() {
  if (tickerFn) {
    gsap.ticker.remove(tickerFn);
    tickerFn = null;
  }
  if (lenisInstance) {
    lenisInstance.destroy();
    lenisInstance = null;
  }
}
